import React, { useEffect, useMemo, useRef, useState } from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatFlexible,
  parseLocaleNumber,
  toInputText,
} from "../../utils/numberFormat";

export type NumberFieldProps = Omit<
  TextFieldProps,
  "type" | "select" | "multiline" | "value"
> & {
  /** Numero en notacion canonica (punto decimal, sin miles) o numero JS. */
  value?: string | number | null;
};

const asCanonical = (value: NumberFieldProps["value"]) =>
  value === null || value === undefined ? "" : String(value);

/**
 * Input numerico en castellano. Sustituye a `<TextField type="number">`.
 *
 * - Se escribe con coma o con punto como decimal ("12,5" y "12.5" valen igual).
 * - Lo que se ve siempre lleva coma decimal y nunca separador de miles, para
 *   que "1.234" no pueda leerse como mil doscientos treinta y cuatro.
 * - Si el texto no es un numero ("1,2,3", "12a") el campo se pone en rojo y
 *   avisa. Si es ambiguo ("1.234") se toma como decimal y se avisa en naranja.
 * - Hacia el formulario sale siempre la notacion canonica ("12.5"), asi que el
 *   resto del codigo sigue haciendo Number(value) como antes. Un texto no
 *   valido sale como "" (nunca un numero a medias).
 *
 * `onChange` recibe un evento cuyo `target.value` es ese valor canonico.
 */
const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  inputProps,
  FormHelperTextProps,
  disabled,
  ...rest
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(() => toInputText(value));
  const lastEmitted = useRef(asCanonical(value));

  // Un cambio que no viene de lo que se acaba de escribir (el formulario
  // recalcula, se resetea...) sustituye al texto.
  useEffect(() => {
    const incoming = asCanonical(value);
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setText(toInputText(incoming));
    }
  }, [value]);

  const parsed = useMemo(() => parseLocaleNumber(text), [text]);
  const invalid = parsed.status === "invalid" && !disabled;
  const ambiguous = parsed.status === "ambiguous" && !disabled;

  const handleChange: NonNullable<TextFieldProps["onChange"]> = (event) => {
    const nextText = event.target.value;
    const next = parseLocaleNumber(nextText);
    const canonical = next.status === "invalid" ? "" : next.canonical;
    setText(nextText);
    lastEmitted.current = canonical;
    if (!onChange) return;
    onChange(
      Object.create(event, {
        target: {
          value: {
            name: event.target.name,
            id: event.target.id,
            value: canonical,
          },
        },
      })
    );
  };

  const handleBlur: NonNullable<TextFieldProps["onBlur"]> = (event) => {
    if (parsed.status === "valid" || parsed.status === "ambiguous") {
      setText(toInputText(parsed.canonical));
    }
    onBlur?.(event);
  };

  let shownHelper = helperText;
  let helperColor: string | undefined;
  if (invalid) {
    shownHelper = t("numberField.invalid");
  } else if (ambiguous) {
    shownHelper = t("numberField.ambiguous", {
      value: formatFlexible(parsed.value ?? 0, 6),
      thousands: parsed.canonical.replace(".", ""),
    });
    helperColor = "warning.main";
  }

  return (
    <TextField
      {...rest}
      type="text"
      disabled={disabled}
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(error) || invalid}
      helperText={shownHelper}
      FormHelperTextProps={{
        ...FormHelperTextProps,
        ...(helperColor && !invalid
          ? { sx: { color: helperColor, fontWeight: 500 } }
          : {}),
      }}
      inputProps={{
        autoComplete: "off",
        ...inputProps,
        inputMode: "decimal",
      }}
    />
  );
};

export default NumberField;
